import type { ReactNode } from 'react';
import { assert } from '@chainflip/utils/assertion';
import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import LayersIcon from '@/shared/assets/svg/layers-icon-with-shadow.svg';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { LimitOrderRow } from '@/shared/components/orders/LimitOrderRow';
import { RangeOrderRow } from '@/shared/components/orders/RangeOrderRow';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { getCachePoolsQuery } from '@/shared/graphql/cache/pools';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { type RpcOrder } from '@/shared/utils/orders';
import { liquidityToTokenAmounts } from '@/shared/utils/tickMath';

export type OpenOrderItem = RpcOrder & {
  createdAt?: string;
};

export const RpcLimitOrderRow = ({
  order,
  baseAsset,
  displayLp,
  displayCreationTimestamp,
  onClick,
  isMobileView = false,
  additionalCells,
}: {
  order: OpenOrderItem;
  baseAsset: BaseChainflipAsset;
  displayLp?: boolean;
  displayCreationTimestamp?: boolean;
  onClick?: (asset: BaseChainflipAsset) => void;
  isMobileView?: boolean;
  additionalCells?: ReactNode;
}) => {
  assert(order.type === 'bid' || order.type === 'ask', 'Invalid order type');
  return (
    <LimitOrderRow
      remainingSellAmount={order.sell_amount}
      originalSellAmount={order.original_sell_amount}
      tick={order.tick}
      limitType={order.type === 'bid' ? 'Buy' : 'Sell'}
      isOrderOpen
      baseAsset={baseAsset}
      lpAccount={displayLp ? order.lp : null}
      creationTimestamp={order.createdAt}
      displayCreationTimestamp={displayCreationTimestamp}
      onClick={onClick}
      isMobileView={isMobileView}
      additionalCells={additionalCells}
    />
  );
};

export const RpcRangeOrderRow = ({
  order,
  baseAsset,
  displayLp,
  displayCreationTimestamp = true,
  onClick,
  isMobileView = false,
  additionalCells,
}: {
  order: OpenOrderItem;
  baseAsset: BaseChainflipAsset;
  displayLp?: boolean;
  displayCreationTimestamp?: boolean;
  onClick?: (asset: BaseChainflipAsset) => void;
  isMobileView?: boolean;
  additionalCells?: ReactNode;
}) => {
  assert(order.type === 'range', 'Invalid order type');
  const { data: poolData } = useGqlQuery(getCachePoolsQuery, {
    variables: { baseAsset },
    context: { clientName: 'statechainCache' },
    refetchInterval: TABLE_POLL_INTERVAL,
  });
  const currentRangeOrderPrice = poolData?.pools?.nodes[0]?.rangeOrderPrice;
  const liquidityFeeHundredthPips = poolData?.pools?.nodes[0]?.liquidityFeeHundredthPips;

  const tokenAmounts = currentRangeOrderPrice
    ? liquidityToTokenAmounts({
        liquidity: order.liquidity.toString(),
        baseAsset,
        currentRate: currentRangeOrderPrice,
        lowerTick: order.range.start,
        upperTick: order.range.end,
      })
    : undefined;

  return (
    <RangeOrderRow
      baseAmount={tokenAmounts?.baseAsset.toBigInt()}
      quoteAmount={tokenAmounts?.quoteAsset.toBigInt()}
      liquidityFeeHundredthPips={liquidityFeeHundredthPips}
      lowerTick={order.range.start}
      upperTick={order.range.end}
      baseAsset={baseAsset}
      lpAccount={displayLp ? order.lp : null}
      currentRangeOrderPrice={currentRangeOrderPrice ?? 0}
      creationTimestamp={order.createdAt}
      displayCreationTimestamp={displayCreationTimestamp}
      isRpcOrderOpen
      onClick={onClick}
      isMobileView={isMobileView}
      additionalCells={additionalCells}
      baseFeesEarned={order.fees_earned.base}
      quoteFeesEarned={order.fees_earned.quote}
    />
  );
};

export default function RpcOrdersTable({
  orders,
  displayLp,
}: {
  orders?: OpenOrderItem[];
  displayLp: boolean;
}) {
  return (
    <TableV2
      columns={[
        'Type',
        'Assets / Amount',
        'Price / Range',
        ...(displayLp ? ['Liquidity Provider'] : []),
        'Status',
      ]}
      emptyRowsContent={
        <div className="m-10 flex flex-col items-center gap-y-2 py-4">
          <LayersIcon />
          <span className="flex flex-col items-center text-14 text-cf-light-2">
            No open orders.
          </span>
        </div>
      }
      rows={
        orders?.map((order) =>
          order.type === 'range' ? (
            <RpcRangeOrderRow
              baseAsset={order.asset}
              order={order}
              displayLp={displayLp}
              key={order.type + order.asset + order.id}
              displayCreationTimestamp={false}
            />
          ) : (
            <RpcLimitOrderRow
              baseAsset={order.asset}
              order={order}
              displayLp={displayLp}
              key={order.type + order.asset + order.id}
              displayCreationTimestamp={false}
            />
          ),
        ) || []
      }
    />
  );
}
